/**
 * Memory Tab — Phase 4 of dashboard NeuralGraph cutover.
 *
 * Reads from `/api/v1/neuralgraph/episodes` and `/api/v1/neuralgraph/entities`
 * (proxied to data_platform). Falls back to legacy `/api/v1/memory/*` if
 * NeuralGraph is unreachable.
 *
 * Removed: Create/Edit/Delete CRUD forms. Episodes decay by TTL in NeuralGraph,
 * and entities are reinforced (not edited) via the Reinforce button.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { observer } from 'mobx-react-lite'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  CircleStackIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  BoltIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  EyeIcon,
} from '@heroicons/react/24/outline'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import { apiClient } from '@/services/api'
import { dataStore } from '@/stores/DataStore'
import toast from 'react-hot-toast'
import clsx from 'clsx'

type SubTab = 'episodes' | 'entities'

const PAGE_SIZE = 50

function relTime(iso?: string): string {
  if (!iso) return '—'
  try {
    const ms = Date.now() - new Date(iso).getTime()
    if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`
    if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`
    return `${Math.floor(ms / 86_400_000)}d ago`
  } catch { return iso }
}

function ttlRemaining(iso?: string, ttl_days = 14): string {
  if (!iso) return '—'
  try {
    const written = new Date(iso).getTime()
    const expires = written + ttl_days * 86_400_000
    const ms = expires - Date.now()
    if (ms <= 0) return 'expired'
    const days = Math.floor(ms / 86_400_000)
    if (days >= 1) return `${days}d left`
    const hrs = Math.floor(ms / 3_600_000)
    return `${hrs}h left`
  } catch { return '—' }
}

const MemoryTab: React.FC = observer(() => {
  const [subTab, setSubTab] = useState<SubTab>('episodes')
  const [search, setSearch] = useState('')
  const [agentFilter, setAgentFilter] = useState<string>('')
  const [tagFilter, setTagFilter] = useState<string>('')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const queryClient = useQueryClient()

  const projectId = dataStore.selectedProjectId || undefined

  // Health check — used to decide when to show fallback alert
  const { data: health } = useQuery({
    queryKey: ['neuralgraph-health-memory'],
    queryFn: () => apiClient.getNeuralGraphHealth(),
    refetchInterval: 60_000,
    retry: false,
  })
  const ngReachable = health?.reachable !== false

  // Episodes via NeuralGraph
  const episodesQ = useQuery({
    queryKey: ['ng-episodes', { agentFilter, tagFilter, projectId }],
    queryFn: () => apiClient.getNeuralGraphEpisodes({
      agent_name: agentFilter || undefined,
      tags: tagFilter ? [tagFilter] : undefined,
      project_id: projectId,
      limit: PAGE_SIZE,
    }),
    enabled: subTab === 'episodes' && ngReachable,
    staleTime: 15_000,
  })

  // Entities via NeuralGraph (semantic ANCHOR)
  const entitiesQ = useQuery({
    queryKey: ['ng-entities', { search, projectId }],
    queryFn: () => apiClient.getNeuralGraphEntities({
      query: search || '*',
      limit: PAGE_SIZE,
      project_id: projectId,
    }),
    enabled: subTab === 'entities' && ngReachable,
    staleTime: 15_000,
  })

  // Fallback: degraded mode when NG unreachable — surfaces cached read-only
  // data from /api/v1/memory/* (which is also engine-backed since 2026-05-08)
  useEffect(() => {
    if (!ngReachable) {
      dataStore.fetchEpisodes()
      dataStore.fetchEntities()
    }
  }, [ngReachable])

  const reinforce = useMutation({
    mutationFn: (memory_id: string) =>
      apiClient.neuralGraphReinforce([memory_id], 1.2, 'manual reinforce from Memory tab'),
    onSuccess: (data, memory_id) => {
      toast.success(`Reinforced ${memory_id.slice(0, 8)}…`)
      queryClient.invalidateQueries({ queryKey: ['ng-entities'] })
    },
    onError: () => toast.error('Reinforce failed'),
  })

  // Distinct agent names for filter dropdown — derived from current page
  const agentOptions = useMemo(() => {
    const set = new Set<string>()
    episodesQ.data?.episodes.forEach(e => e.agent_name && set.add(e.agent_name))
    return Array.from(set).sort()
  }, [episodesQ.data])

  const toggleExpanded = useCallback((id: string) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }, [])

  const handleRefresh = () => {
    if (subTab === 'episodes') episodesQ.refetch()
    else entitiesQ.refetch()
  }

  return (
    <div className="space-y-4">
      {/* Header + sub-tabs + refresh */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <CircleStackIcon className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Memory</h2>
          <Badge variant={ngReachable ? 'success' : 'warning'}>
            {ngReachable ? 'NeuralGraph live' : 'NeuralGraph degraded'}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg bg-muted p-0.5">
            <button
              onClick={() => setSubTab('episodes')}
              className={clsx(
                'px-3 py-1 text-sm font-medium rounded-md transition-all',
                subTab === 'episodes' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
              )}
            >
              Episodes
              {episodesQ.data && (
                <span className="ml-1 text-[10px] text-muted-foreground">({episodesQ.data.total_returned})</span>
              )}
            </button>
            <button
              onClick={() => setSubTab('entities')}
              className={clsx(
                'px-3 py-1 text-sm font-medium rounded-md transition-all',
                subTab === 'entities' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
              )}
            >
              Entities
              {entitiesQ.data && (
                <span className="ml-1 text-[10px] text-muted-foreground">({entitiesQ.data.total_returned})</span>
              )}
            </button>
          </div>
          <Button variant="secondary" size="sm" onClick={handleRefresh}>
            <ArrowPathIcon className={clsx('w-4 h-4', (episodesQ.isFetching || entitiesQ.isFetching) && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* Degraded fallback banner */}
      {!ngReachable && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400 text-sm">
          <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">NeuralGraph unreachable — running in degraded mode.</p>
            <p className="text-xs mt-1 opacity-80">
              Showing cached read-only data from `/api/v1/memory/*`. Reinforce + filters disabled until NeuralGraph is back.
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <Card className="p-3">
        <div className="flex items-end gap-2 flex-wrap">
          {subTab === 'entities' && (
            <div className="flex-1 min-w-[200px]">
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">
                Semantic search (ANCHOR)
              </label>
              <div className="flex gap-1">
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && entitiesQ.refetch()}
                  placeholder="e.g. objection handling, project health"
                  className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-card"
                />
                <Button variant="secondary" size="sm" onClick={() => entitiesQ.refetch()}>
                  <MagnifyingGlassIcon className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
          {subTab === 'episodes' && (
            <>
              <div className="w-48">
                <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">
                  Agent
                </label>
                <select
                  value={agentFilter}
                  onChange={e => setAgentFilter(e.target.value)}
                  className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-card"
                  disabled={!ngReachable}
                >
                  <option value="">all agents</option>
                  {agentOptions.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div className="w-32">
                <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">
                  Tag
                </label>
                <select
                  value={tagFilter}
                  onChange={e => setTagFilter(e.target.value)}
                  className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-card"
                  disabled={!ngReachable}
                >
                  <option value="">all tags</option>
                  <option value="execution">execution</option>
                  <option value="audit">audit</option>
                  <option value="summary">summary</option>
                  <option value="offloaded">offloaded</option>
                </select>
              </div>
              {(agentFilter || tagFilter) && (
                <button
                  onClick={() => { setAgentFilter(''); setTagFilter('') }}
                  className="text-xs text-muted-foreground hover:text-foreground py-1.5"
                >
                  clear
                </button>
              )}
            </>
          )}
        </div>
      </Card>

      {/* Episodes list */}
      {subTab === 'episodes' && (
        <Card className="p-0">
          {episodesQ.isLoading ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
          ) : episodesQ.isError ? (
            <div className="p-6 text-center text-sm text-red-500">Episodes query failed</div>
          ) : !ngReachable && dataStore.episodes.length > 0 ? (
            <LegacyEpisodesList episodes={dataStore.episodes} />
          ) : episodesQ.data && episodesQ.data.episodes.length > 0 ? (
            <ul className="divide-y divide-border">
              {episodesQ.data.episodes.map(e => (
                <li key={e.id} className="p-3 hover:bg-muted/30">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-foreground">{e.agent_name || 'unknown'}</span>
                        {e.episode_type && <Badge variant="gray">{e.episode_type}</Badge>}
                        {e.tags?.map(t => (
                          <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-600">
                            {t}
                          </span>
                        ))}
                        {e.quality_score != null && (
                          <span className={clsx(
                            'text-[10px] tabular-nums px-1.5 py-0.5 rounded',
                            e.quality_score >= 8 ? 'bg-emerald-500/15 text-emerald-600'
                              : e.quality_score >= 5 ? 'bg-amber-500/15 text-amber-600'
                              : 'bg-red-500/15 text-red-600'
                          )}>
                            quality {e.quality_score.toFixed(1)}/10
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                        <span title={e.timestamp}>{relTime(e.timestamp)}</span>
                        <span className="flex items-center gap-0.5">
                          <ClockIcon className="w-3 h-3" />
                          {ttlRemaining(e.timestamp, 14)}
                        </span>
                        {e.project_id && e.project_id !== 'default' && (
                          <span className="font-mono">project {e.project_id.slice(0, 8)}…</span>
                        )}
                        <code className="ml-auto font-mono text-[10px]">{e.id.slice(0, 8)}…</code>
                      </div>
                      <button
                        onClick={() => toggleExpanded(e.id)}
                        className="mt-2 text-xs text-foreground hover:text-primary inline-flex items-center gap-1"
                      >
                        <EyeIcon className="w-3 h-3" />
                        {expanded.has(e.id) ? 'hide' : 'show'} content
                      </button>
                      {expanded.has(e.id) && e.content && (
                        <pre className="mt-2 text-[11px] font-mono whitespace-pre-wrap break-words bg-muted/40 rounded p-2 max-h-48 overflow-y-auto">
                          {typeof e.content === 'string' ? e.content : JSON.stringify(e.content, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No episodes match the current filters
            </div>
          )}
        </Card>
      )}

      {/* Entities list */}
      {subTab === 'entities' && (
        <Card className="p-0">
          {entitiesQ.isLoading ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Searching…</div>
          ) : entitiesQ.isError ? (
            <div className="p-6 text-center text-sm text-red-500">Entity search failed</div>
          ) : !ngReachable && dataStore.entities.length > 0 ? (
            <LegacyEntitiesList entities={dataStore.entities} />
          ) : entitiesQ.data && entitiesQ.data.entities.length > 0 ? (
            <ul className="divide-y divide-border">
              {entitiesQ.data.entities.map(en => (
                <li key={en.memory_id} className="p-3 hover:bg-muted/30">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-foreground truncate">
                          {en.name || en.memory_id.slice(0, 12)}
                        </span>
                        {en.entity_type && <Badge variant="primary">{en.entity_type}</Badge>}
                        {en.domain && <Badge variant="gray">{en.domain}</Badge>}
                        {en.score != null && (
                          <span className="text-[10px] text-muted-foreground tabular-nums">
                            score {en.score.toFixed(3)}
                          </span>
                        )}
                      </div>
                      {en.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{en.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground">
                        <code className="font-mono">{en.memory_id.slice(0, 16)}…</code>
                        {en.confidence != null && <span>confidence {(en.confidence * 100).toFixed(0)}%</span>}
                        {en.layer && <span>{en.layer}</span>}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => reinforce.mutate(en.memory_id)}
                      disabled={!ngReachable || reinforce.isPending}
                      title="Strengthen this entity's edges (REINFORCE)"
                    >
                      <BoltIcon className="w-3 h-3 mr-1" />
                      Reinforce
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No entities found{search ? ` for "${search}"` : ''}
            </div>
          )}
        </Card>
      )}
    </div>
  )
})

// ── Legacy fallback (read-only) ────────────────────────────────────────

function LegacyEpisodesList({ episodes }: { episodes: any[] }) {
  return (
    <ul className="divide-y divide-border">
      {episodes.slice(0, 50).map((e: any) => (
        <li key={e.id} className="p-3">
          <div className="flex items-center gap-2">
            <Badge variant="gray">{e.episode_type || 'unknown'}</Badge>
            {e.domain && <Badge variant="primary">{e.domain}</Badge>}
            <span className="text-[10px] text-muted-foreground ml-auto">{relTime(e.timestamp)}</span>
          </div>
          <p className="text-xs text-foreground mt-1 line-clamp-2">{e.content}</p>
        </li>
      ))}
    </ul>
  )
}

function LegacyEntitiesList({ entities }: { entities: any[] }) {
  return (
    <ul className="divide-y divide-border">
      {entities.slice(0, 50).map((e: any) => (
        <li key={e.id} className="p-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground">{e.name}</span>
            {e.entity_type && <Badge variant="primary">{e.entity_type}</Badge>}
            {e.domain && <Badge variant="gray">{e.domain}</Badge>}
          </div>
          {e.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{e.description}</p>}
        </li>
      ))}
    </ul>
  )
}

export default MemoryTab
